import { Breakpoint, COLUMN, Level } from "../Performance.model";
import useBreakPoint from "../../../utils/useBreakPoint";
import { Card, Table, Toolbar, MetricText, BodyRow, MetricCell, Legend, ToolbarActions, TableWrapper, FootText } from "../components/commonComponents";
import EmptyDataCard from "../components/EmptyDataCard";
import TableHeader from "../components/TableHeader";
import { useTranslation } from "react-i18next";
import LoadingCard from "../components/LoadingCard";
import { COLORS } from "../../../utils/colors";
import { formatTimeLabel } from "../../../utils/dateUtils";


interface BaseGenericRow {
  id: string;
  label?: string;
  badge?: string;
  importedDateTime?: string;
  [key: string]: any;
}

const boldkeys = ["ca", "caMiroir", "streamMiroir", "varN1Miroir"];
const bluekeys = ["caMiroir", "streamMiroir", "varN1Miroir"];
const withBorderkeys = ["hour"];
const hourkeys = ["hour"];

function PercentBadge({ value, isBold, isBlue, isHour }: { value: string, isBold?: boolean, isBlue?: boolean, isHour?: boolean }) {
  const isNeg = value?.startsWith("-") && value !== "-";
  const isPos = value?.startsWith("+");
  return (
      <MetricText
          style={{
            color: isHour
                ? "#002B47"
                : isBlue
                    ? "#0970E6"
                    : isNeg ? COLORS.red
                        : isPos ? COLORS.green
                            : "#696969",
            fontWeight: isBold ? 700 : 400,
            fontSize: isBlue ? "var(--font-size-body-m, 16px)" : undefined,
            lineHeight: isBlue ? "var(--line-height-body-m, 24px)" : undefined,
          }}
      >
        {value}
      </MetricText>
  );
}

interface TableRowProps<ROW extends BaseGenericRow> {
  row: ROW;
  metricKeys?: string[];
  index?: number;
  visibleMetricKeys: string[];
}

function TableRow<ROW extends BaseGenericRow>({ row, visibleMetricKeys, index }: TableRowProps<ROW>) {
  return (
    <>
      <BodyRow $variant={"default"} $clickable={Boolean(false)} $index={index}>
              {visibleMetricKeys.map((metricKey) => (
                <MetricCell style={withBorderkeys.includes(metricKey) ? 
                  { 
                  'width': '68px',
                  'minWidth': '68px',
                  borderRight: '1px solid #e2e8f0'}
                  : {}} 
                  key={metricKey}>
                    <PercentBadge
                        isBold={boldkeys.includes(metricKey)}
                        isBlue={bluekeys.includes(metricKey)}
                        isHour={hourkeys.includes(metricKey)}
                        value={row[metricKey]} />
                    </MetricCell>
              ))}
      </BodyRow>
    </>
  );
}

interface PerformanceViewProps<T extends BaseGenericRow> {
  data: T[];
  title?: string;
  levels?: Level[];
  metrics?:  COLUMN[];
  isLoading?: boolean;
}

export default function PerformanceDetailView<T extends BaseGenericRow>({
  data,
  metrics = [],
  isLoading
}: PerformanceViewProps<T>) {
  const breakPoint = useBreakPoint() as Breakpoint;
  const visibleColumns = metrics.filter((column) => column.isVisible(breakPoint as any))
  const visibleMetricKeys = visibleColumns.map((column) => column.key);
  const { t } = useTranslation();

  if (isLoading) {
    return <LoadingCard />;
  }

  if (!data?.length) {
    return <EmptyDataCard/>
  }

    return (
      <Card>
        <TableWrapper>
          <Table>
            <TableHeader visibleColumns={visibleColumns} title=""/>
            <tbody>
              {data.map((row, index) => (
                <TableRow
                  index={!row.level ? index : undefined}
                  key={row.id}
                  row={row}
                  visibleMetricKeys={visibleMetricKeys}
                />
              ))}
            </tbody>
          </Table>
        </TableWrapper>
        {!!data[0]?.importedDateTime &&
         <Toolbar>
          <Legend>
            <FootText>
              {`${t('commercial.data-updated')} ${formatTimeLabel(data[0].importedDateTime)}`}
            </FootText>
          </Legend>

          <ToolbarActions>
            <FootText>
              {`${t('commercial.total-lignes')} ${data.length}`}
            </FootText>
          </ToolbarActions>
        </Toolbar>}
      </Card>
    );
}
